import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { UserService } from '@duck/api-definitions'
import { Input } from '@duck/ui'
import { useTar } from '@/modules'
import { TranslationKey } from '@/modules/i18n/types'
import { apis } from '@/modules/apiClients'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useUserDetails } from '@/hooks/useUserDetails'
import { UserBadge } from '../UserBadge/UserBadge'
import { ChallengesSection } from '../AdminUsers/UserDetailSections/ChallengesSection'
import { StandardLoader } from '../StandardLoader'
import { NoDataEmptyState } from '../EmptyStates'

// Same enum→label mapping as OverviewSection/PersonalInfoDialog.
const PROFESSION_LABELS: Partial<Record<UserService.Profession, TranslationKey>> = {
  [UserService.Profession.DataScientist]: 'personalInfo.professionOptions.dataScientist',
  [UserService.Profession.DataAnalyst]: 'personalInfo.professionOptions.dataAnalyst',
  [UserService.Profession.DataEngineer]: 'personalInfo.professionOptions.dataEngineer',
  [UserService.Profession.DataOperations]: 'personalInfo.professionOptions.dataOperations',
  [UserService.Profession.MachineLearningEngineer]: 'personalInfo.professionOptions.machineLearningEngineer',
  [UserService.Profession.SoftwareEngineer]: 'personalInfo.professionOptions.softwareEngineer',
  [UserService.Profession.RecruitmentManager]: 'personalInfo.professionOptions.recruitmentManager',
  [UserService.Profession.Other]: 'personalInfo.professionOptions.other',
}

const COLUMN_COUNT = 7

const UserChallengesRow: React.FC<{ userId: string }> = ({ userId }) => {
  const { t } = useTar()
  const { challenges, isLoading, isError } = useUserDetails(userId)

  return (
    <tr className="border-b border-border/50">
      <td colSpan={COLUMN_COUNT} className="bg-muted/5 p-3">
        {isLoading ? (
          <div className="py-4 text-center text-sm text-muted-foreground">{t('common.loading')}</div>
        ) : isError ? (
          <div className="py-4 text-center text-sm text-error">{t('adminAnonymousSharing.challengesLoadError')}</div>
        ) : (
          <ChallengesSection challenges={challenges ?? []} />
        )}
      </td>
    </tr>
  )
}

export const AnonymousSharingPage: React.FC = () => {
  const { t } = useTar()
  useDocumentTitle('Anonymous Sharing', 'admin')
  const [expandedUserIds, setExpandedUserIds] = useState<ReadonlySet<string>>(new Set())
  const [nameFilter, setNameFilter] = useState('')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['adminAnonymousSharingUsers'],
    queryFn: async () => {
      const response = await apis.userService.adminAnonymousSharingUsersGet()
      return response.data
    },
  })

  const toggleExpanded = (userId: string) =>
    setExpandedUserIds((current) => {
      const next = new Set(current)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return next
    })

  const allUsers = data?.users ?? []
  const query = nameFilter.trim().toLowerCase()
  const filteredUsers =
    query === ''
      ? allUsers
      : allUsers.filter((user) =>
          `${user.firstName ?? ''} ${user.lastName ?? ''}`.toLowerCase().includes(query)
        )

  if (isLoading) return <StandardLoader />

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">{t('adminAnonymousSharing.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('adminAnonymousSharing.description')}</p>
      </div>

      {isError ? (
        <div className="py-8 text-center text-sm text-error">{t('adminAnonymousSharing.loadError')}</div>
      ) : allUsers.length === 0 ? (
        <NoDataEmptyState title={t('adminAnonymousSharing.empty')} />
      ) : (
        <div className="space-y-4">
          <Input
            type="search"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder={t('adminAnonymousSharing.searchPlaceholder')}
            className="max-w-xs"
          />
          {filteredUsers.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {t('adminAnonymousSharing.noMatches')}
            </div>
          ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="text-start py-2 px-3 font-medium">{t('common.user')}</th>
                <th className="text-start py-2 px-3 font-medium">{t('common.country')}</th>
                <th className="text-start py-2 px-3 font-medium">{t('adminAnonymousSharing.yearsOfXP')}</th>
                <th className="text-start py-2 px-3 font-medium">{t('personalInfo.profession')}</th>
                <th className="text-start py-2 px-3 font-medium">{t('adminAnonymousSharing.tasksStarted')}</th>
                <th className="text-start py-2 px-3 font-medium">{t('adminAnonymousSharing.tasksFinished')}</th>
                <th className="text-start py-2 px-3 font-medium">{t('adminAnonymousSharing.averageGrade')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const professionKey =
                  user.profession !== undefined ? PROFESSION_LABELS[user.profession] : undefined
                return (
                  <React.Fragment key={user.userId}>
                    <tr
                      onClick={() => toggleExpanded(user.userId)}
                      className="cursor-pointer border-b border-border/50 hover:bg-muted/5"
                    >
                      <td className="py-2 px-3">
                        {/* stopPropagation on the badge only: the badge has its own click-through
                            to the admin user page; the rest of the cell still toggles the row. */}
                        <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
                          <UserBadge
                            userId={user.userId}
                            firstName={user.firstName}
                            lastName={user.lastName}
                            role={user.role}
                            avatar={user.avatar}
                            email={user.email}
                          />
                        </span>
                      </td>
                      <td className="py-2 px-3">{user.country ?? '-'}</td>
                      <td className="py-2 px-3 tabular-nums">{user.yearsOfXP ?? '-'}</td>
                      <td className="py-2 px-3">{professionKey !== undefined ? t(professionKey) : '-'}</td>
                      <td className="py-2 px-3 tabular-nums">{user.tasksStarted}</td>
                      <td className="py-2 px-3 tabular-nums">{user.tasksFinished}</td>
                      <td className="py-2 px-3 tabular-nums">{user.averageGrade !== undefined ? user.averageGrade.toFixed(1) : '-'}</td>
                    </tr>
                    {expandedUserIds.has(user.userId) && <UserChallengesRow userId={user.userId} />}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
          )}
        </div>
      )}
    </div>
  )
}
